/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import FirmwarePackages from "./FirmwarePackages";
import FirmwareChangelog from "./FirmwareChangelog";
import DownloadDeviceDescriptions from "./DownloadDeviceDescriptions";
import { brandId } from "./brand";
import { makeCdnUrl } from "motion-master-client";
import { tabs } from "./utils";
import FunctionBlocks from "./FunctionBlocks";

const normalizeVersion = (v) => v?.replace(/^v/i, "") || "";

const FirmwareBundlesModalContent = ({
  oblacDrivesVersion,
  firmwareVersion,
  activeTab: propActiveTab = "packages",
  releases = [],
  showDownloadBundle = true,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(propActiveTab);
  const [selectedFirmwareVersion, setSelectedFirmwareVersion] = useState("");
  const [packageCount, setPackageCount] = useState(0);

  const currentRelease = useMemo(() => {
    if (!releases?.length) return null;
    return (
      releases.find((r) => r.version === oblacDrivesVersion) || releases[0]
    );
  }, [releases, oblacDrivesVersion]);

  const firmwareOptions = useMemo(() => {
    return (currentRelease?.firmwares ?? []).map((fw) =>
      typeof fw === "string" ? fw : (fw.version ?? String(fw)),
    );
  }, [currentRelease]);

  useEffect(() => {
    if (!firmwareOptions.length) {
      setSelectedFirmwareVersion("");
      return;
    }

    if (firmwareVersion === undefined) {
      setSelectedFirmwareVersion("");
      return;
    }

    if (firmwareVersion === "latest") {
      setSelectedFirmwareVersion(firmwareOptions[0]);
      return;
    }

    const found = firmwareOptions.find(
      (f) => normalizeVersion(f) === normalizeVersion(firmwareVersion),
    );
    setSelectedFirmwareVersion(found || "");
  }, [firmwareOptions, firmwareVersion]);

  const firmwareInvalid = !selectedFirmwareVersion;
  const normalizedFirmware = normalizeVersion(selectedFirmwareVersion);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (normalizedFirmware) {
      navigate(`/${currentRelease?.version}/${normalizedFirmware}/${tab}`);
    }
  };

  const handleSelectChange = (e) => {
    const value = e.target.value;
    setSelectedFirmwareVersion(value);
    const normalized = normalizeVersion(value);
    navigate(`/${currentRelease?.version}/${normalized}/${activeTab}`);
  };

  return (
    <div className="d-flex flex-column gap-3">
      {firmwareInvalid && (
        <div className="alert alert-warning m-0">
          The firmware version you selected is not available for this OBLAC
          Drives release. <br />
          Please select a valid firmware version for release{" "}
          <strong>{currentRelease?.version ?? oblacDrivesVersion}</strong>.
        </div>
      )}

      <form className="form-group d-flex w-100">
        <div className="w-100">
          <label htmlFor="firmware-version-select" className="d-none">
            Firmware{" "}
            {firmwareOptions.length > 0 &&
              `(${firmwareOptions.length} versions available)`}
          </label>
          <select
            id="firmware-version-select"
            className="form-select form-select-lg border-dark"
            value={selectedFirmwareVersion}
            onChange={handleSelectChange}
            disabled={!firmwareOptions.length}
          >
            <option value="" disabled>
              Select a firmware version...
            </option>
            {firmwareOptions.map((label) => (
              <option key={normalizeVersion(label)} value={label}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </form>

      {!firmwareInvalid && (
        <>
          <ul className="nav nav-tabs">
            {tabs.map(({ key, label }) => (
              <li className="nav-item" key={key}>
                <a
                  className={`nav-link ${activeTab === key ? "active" : ""}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleTabChange(key)}
                >
                  {key === 'packages' ? `${label} (${packageCount})` : label}
                </a>
              </li>
            ))}
          </ul>

          <div className="tab-content">
            <div
              className={`tab-pane fade ${
                activeTab === "packages" ? "show active" : ""
              }`}
            >
              <FirmwarePackages
                key={selectedFirmwareVersion}
                version={selectedFirmwareVersion}
                showDownloadBundle={showDownloadBundle}
                isActive={activeTab === "packages"}
                onPackageCountChange={setPackageCount}
              />
            </div>
            <div
              className={`tab-pane fade ${
                activeTab === "changelog" ? "show active" : ""
              }`}
            >
              <FirmwareChangelog
                key={selectedFirmwareVersion}
                version={selectedFirmwareVersion}
                isActive={activeTab === "changelog"}
              />
            </div>
            <div
              className={`tab-pane fade ${
                activeTab === "deviceDescriptions" ? "show active" : ""
              }`}
            >
              <DownloadDeviceDescriptions
                key={selectedFirmwareVersion}
                brandId={brandId}
                version={selectedFirmwareVersion}
              />
            </div>
            <div
              className={`tab-pane fade ${
                activeTab === "functionBlocks" ? "show active" : ""
              }`}
            >
              <FunctionBlocks
                brandId={brandId}
                version={selectedFirmwareVersion}
              />
            </div>
          </div>

          {showDownloadBundle && (
            <div
              className="bg-white d-flex justify-content-end"
              style={{
                position: "sticky",
                bottom: 0,
                gap: "0.5rem",
                zIndex: 1,
                marginTop: "1rem",
              }}
            >
              <a
                href={makeCdnUrl(
                  `/brands/${brandId}/bundles/firmwares/somanet-motion-drive_${encodeURIComponent(selectedFirmwareVersion)}.odbx`,
                )}
                className="btn btn-primary d-flex align-items-center"
                title={`Download somanet-motion-drive_${selectedFirmwareVersion}.odbx`}
              >
                Download Firmware Bundle (.odbx)
              </a>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FirmwareBundlesModalContent;
